<template>
    <div class="h-100 content__page">
        <div :class="{ 'min-h-inherit': !groupByKeys.length }">
            <div v-if="showActionBtns" class="content__page__header">
                <div class="list--count">
                    <FormLabel title="Total upcoming meetings" label-type="title">
                        <span class="ml--3 zm__count__badge text--xs">{{ meetings.length }}</span>
                    </FormLabel>
                </div>
                <div class="flex align-items-center">
                    <BaseButton 
                        label="+ New meeting"
                        class="mr--5"
                        :loading="fetchCalendarsState === 'loading'"
                        @submit="openCreateMeeting"
                    />
                    <MainFilter v-if="meetings.length" page-type="meetings" />
                </div>
            </div>

            <div class="zm__content">
                <div class="zm__content__left" :class="{ 'empty': !groupByKeys.length }">
                    <template v-if="pageState.loading">
                        <PageSpinner />
                    </template>

                    <EmptyPage v-else-if="pageState.err" state="error">
                        <template #button>
                            <BaseButton
                                label="Reload" 
                                icon-name="refresh"
                                @submit="handleFetchMeetings"
                            />
                        </template> 
                    </EmptyPage>

                    <template v-else>
                        <template v-if="!groupByKeys.length">
                            <EmptyPage 
                                v-if="!isSearched"
                                state="empty_custom" 
                                btn-state="custom"
                                icon-name="calendar"
                                position="left"
                                title="No existing meetings" 
                                subtitle="Meetings are a great way to keep track of virtual or physcial appointments with clients. To get started, ensure you have at least one connected calendar via integrations."
                            >
                                <template #button>
                                    <BaseButton 
                                        label="+ New meeting"
                                        @submit="openCreateMeeting"
                                    />
                                </template> 
                            </EmptyPage>
                            <EmptyPage v-else state="no_result" />
                        </template>
                        
                        <template v-else>
                            <MeetingList 
                                :items="meetings"
                                :current-meeting-id="currentMeetingItem?._id"
                                @fetch-single-meeting="handleFetchSingleMeeting"
                            />
                        </template>
                    </template>
                </div>

                <div v-if="groupByKeys.length" class="zm__content__right">
                    <template v-if="currentMeetingItem._id">
                        <SingleMeeting 
                            :current-meeting-item="currentMeetingItem" 
                            :page-loading="currentItemState.loading"
                            :item-loading="deleteLoading"
                            :err="currentItemState.err"
                            :note-action="noteAction"
                            :current-note="currentNote"
                            @open-note="openNoteModal"
                            @open-delete="openDeleteMeetingModal"
                            @delete-note="onDeleteCompleteNote" 
                            @open-edit="openEditMeetingModal"
                            @duplicate-complete="onDuplicateComplete"
                        />
                    </template>

                    <EmptyPage 
                        v-else
                        state="empty_custom" 
                        icon-name="calendar"
                        title="No meeting selected" 
                        subtitle="Select a meeting to view meeting details"
                    />
                </div>
            </div>
        </div>

        <MeetingNoteModal 
            :meeting-id="currentMeetingItem?._id" 
            :action="noteAction"
            :show-modal="showNoteModal" 
            :current-note="currentNote"
            @complete="handleNoteComplete" 
            @cancel="closeNoteModal" 
        />

        <ConfirmDeletion
            type="meeting"
            :show-modal="showDeleteModal"
            :loading="deleteLoading"
            @cancel="closeDeleteMeetingModal" 
            @delete="deleteMeeting"
        />

        <CreateMeetingModal 
            :show-modal="modalState === 'showMeetingModal'" 
            :action="meetingModalType"
            :cached-clients="clients"
            modal-type="meetings"
            :selected-meeting="selectedMeeting"
            :calendars="calendarIntegrations"
            @resync="showSyncModal"
            @close="closeMeetingModal"
            @create-complete="onCompleteCreateMeeting" 
            @edit-complete="onCompleteEditMeeting" 
        />

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal|| showUpgradeModalAlt"  
            type="meetings" 
            @cancel="closeUpgradeModal"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free'">
                        <p class="text--sm text-gray-400">
                            This is a paid feature available on the <strong>Standard</strong> and <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-400 mt--15">
                            To unlock this feature, go to settings to upgrade your account plan.
                        </p>
                    </template>

                    <template v-if="accountPlan === 'standard'">
                        <p class="text--sm text-gray-400">
                            You have exhausted the number of meeting allowed on the <strong>Standard</strong> account plan.
                        </p>
                        <p class="text--sm text-gray-400 mt--15">
                            To create more meetings, go to settings to upgrade to a <strong>Plus</strong>.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>

        <MainModal 
            :show-modal="showIntegrationInfoModal" 
            size="sm" 
            title="Connect your calendar"
            @close="showIntegrationInfoModal = false"
        >
            <template #body>
                <div class="f">
                    <p class="text--sm text-gray-600">
                        In order to create a new meeting, you need to have at least one connected calendar.
                    </p>
                    <p class="text--sm mt--20 text-gray-600">
                        To connect your calendar, go to <strong>settings > integrations</strong>. Select a calendar service and enable.
                    </p>
                </div>                    
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end">
                    <BaseButton 
                        label="Go to settings" 
                        variant-style="primary"
                        @submit="goToSettings" 
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'showSyncModal'" 
            size="sm" 
            title="Re-sync your Google calendar"
            @close="closeSyncModal"
        >
            <template #body>
                <div class="f">
                    <p class="text--sm">
                        Your Google calendar seems to be out of sync
                    </p>
                    <p class="text--sm mt--20 text-gray-200">
                        Re-syncing your calendar helps us get access to effictively create a meeting for you on your behalf. Click on the <strong>"Continue"</strong> button to re-sync or cancel to abort.
                    </p>
                </div>                    
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        @submit="closeSyncModal"
                    />
                    <BaseButton 
                        label="Continue" 
                        variant-style="primary"
                        @submit="initiateSync" 
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import MeetingList from "@/components/meetings/MeetingList.vue";
import SingleMeeting from "@/components/meetings/SingleMeeting.vue";
import { 
    IconSvg, 
    Pagination, 
    PageSpinner, 
    EmptyPage,
    BaseButton, 
    MainFilter,
    FormLabel,
    CreateMeetingModal,
    MeetingNoteModal,
    ConfirmDeletion,
    UpgradeAccountModal,
    MainModal,
} from "@/components/ui/index";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import { groupList, assembleQueryList } from "@/utils/helpers";

export default {
    name: "MeetingsView",

    components: {
        IconSvg,
        EmptyPage,
        Pagination, 
        MeetingList,
        MeetingNoteModal,
        PageSpinner,
        BaseButton, 
        CreateMeetingModal,
        UpgradeAccountModal,
        MainFilter, 
        MeetingNoteModal,
        FormLabel,
        SingleMeeting,
        ConfirmDeletion,
        MainModal,
    },

    data() {
        return {
            pageState: {
                loading: false,
                err: false,
                errMsg: "",
            },
            currentMeetingItem: {
                _id: "",
                googleEventId: "",
                title: "",
                description: "",
                startTime: null,
                endTime: null,
                attendees: [],
            },
            currentItemState: {
                loading: false,
                err: false,
                errMsg: "",
            },
            showNoteModal: false,
            noteAction: "add",
            currentNote: undefined,
            deleteLoading: false,
            showDeleteModal: false,
            showUpgradeModalAlt: false,
            calendarLoading: false,
            calendarLoadingErr: false,
            showIntegrationInfoModal: false,
            meetingModalType: "",
            selectedMeeting: null,
            fetchCalendarsState: "default",
            modalState: "",
            tempMeetingData: undefined,
        };
    },

    computed: {
        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },

        meetings() {
            return this.$store.getters["meeting/meetings"];
        },

        pageData() {
            return this.$store.getters["meeting/pageData"];
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        userFullName() {
            return this.profile?.fullName || "";
        },

        userEmail() {
            return this.profile?.email || "";
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        groupedListByDate() {
            if (!this.meetings.length) {
                return [];
            }

            const arr = groupList(this.meetings, (item) => {
                // createdAt comes in form of timestamp from API
                // Convert to YYYY-MM-DD
                const date = new Date(item.createdAt);

                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();

                const filter = `${year}-${month}-${day}`;

                return filter;
            });

            return Object.fromEntries(arr);
        },

        groupByKeys() {
            return Object.keys(this.groupedListByDate) || [];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        selectedCalendar() {
            return this.$store.getters["meeting/selectedCalendar"];
        },

        appIntegrations() {
            return this.$store.getters["config/integrations"] || [];
        },

        userIntegrations() {
            return this.$store.getters["integration/integrations"] || [];
        },

        userIntegrationsMap() {
            const newMap = new Map();

            for (let i = 0; i < this.userIntegrations.length; i++) {
                const item = this.userIntegrations[i];

                if (["google_calendar"].includes(item.key) && item.enabled) {
                    newMap.set(item.key, {
                        _id: item._id,
                        enabled: item.enabled,
                    });
                }
            } 

            return newMap;
        },

        calendarIntegrations() {
            return this.appIntegrations.filter(item => this.userIntegrationsMap.get(item?.key));
        },

        clients() {
            return this.$store.getters["clients/clients"];
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {
                await this.handleFetchMeetings({ refresh: true, ...newVal });
            },
            deep: true,
        },
    },

    async mounted() {
        if (this.meetings.length) {
            const firstItemInArrId = this.meetings[0]?._id;
            await this.handleFetchSingleMeeting(firstItemInArrId);
        } else {
            await this.handleFetchMeetings();
        }
    },
    
    methods: {
        goToSettings() {
            localStorage.setItem("z:auth_type", "meeting");
            return this.$router.push("/dashboard/settings/integrations");
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "meetings");
        },

        async handleFetchMeetings(params = {}, reload = true) {
            try {
                if (reload) {
                    this.pageState.loading = true;
                }

                this.pageState.err = false;
                this.pageState.errMsg = "";
                
                const pageQueryObj = {
                    page: params?.pageNumber || this.pageData.currentPage || 1,
                    ...params,
                };

                if (params.endDate) {
                    let endDate;
                    const currentDate = params?.startDate;

                    switch (params.endDate) {
                    case "a_week_after": // 1 week after the current date
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 7);
                        break;

                    case "two_weeks_after": // 2 weeks after the current date
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 14);
                        break;

                    case "current_month": // The end of the current month
                        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                        break;

                    default:
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 7);
                        break;
                    }
                        
                    pageQueryObj.endDate = endDate;
                }

                const buildResult = assembleQueryList(pageQueryObj);

                await this.$store.dispatch("meeting/fetchMeetings", buildResult);

                const firstItemInArrId = this.meetings[0]?._id;
                await this.handleFetchSingleMeeting(firstItemInArrId);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                this.pageState.err = true;
                this.pageState.errMsg = errMsg;
            } finally {
                if (reload) {
                    this.pageState.loading = false;
                }
            }
        },

        async handleFetchSingleMeeting(id) {
            try {
                if (!id) {
                    throw new Error("Missing meeting id required");
                }

                this.currentItemState.loading = true;
                this.currentItemState.err = false;
                this.currentItemState.errMsg = "";

                const meetingItemRes = await this.$store.dispatch("meeting/fetchOneMeeting", id);

                this.currentMeetingItem = {
                    ...this.currentMeetingItem,
                    ...meetingItemRes,
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.currentItemState.err = true;
                this.currentItemState.errMsg = errMsg;
            } finally {
                this.currentItemState.loading = false;
            }
        },

        openNoteModal({ action, item }) {            
            if (action === "edit" && !item?._id) return;

            this.noteAction = action;

            if (item) {
                this.currentNote = item;
            }
            this.showNoteModal = true;
        },

        closeNoteModal() {
            this.noteAction = "";
            this.showNoteModal = false;
        },

        openDeleteMeetingModal(item) {            
            this.selectedMeeting = item;
            this.showDeleteModal = true;
        },

        closeDeleteMeetingModal() {
            this.selectedMeeting = undefined;
            this.showDeleteModal = false;
        },

        handleNoteComplete(item) {            
            if (this.noteAction === "add") {
                this.currentMeetingItem.notes = [ item, ...this.currentMeetingItem.notes];
            } else {
                const updatedNotes = this.currentMeetingItem.notes.map(x => {                    
                    if (x._id === item._id) {
                        return item;
                    } else {
                        return x;
                    }
                });
                
                this.currentMeetingItem.notes = updatedNotes;
            };
            this.currentNote = undefined;
        },

        async onDeleteCompleteNote(item) {
            try {
                if (!item) {
                    this.$toast.error("Unable to delete note");
                    return;
                }

                this.currentNote = item;
                this.noteAction = "delete";                

                await this.$store.dispatch("meeting/deleteNote", { meetingId: this.currentMeetingItem?._id, noteId: item._id });
                this.currentMeetingItem.notes = this.currentMeetingItem.notes.filter(x => x._id !== item._id);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.noteAction = "";
            }
        },

        async deleteMeeting() {
            if (!this.selectedMeeting._id) return;

            try {
                this.deleteLoading = true;
                
                await this.$store.dispatch("meeting/removeMeeting", this.selectedMeeting._id);
                
                this.$toast.info("Meeting deleted");
                this.closeDeleteMeetingModal();
                this.currentMeetingItem = {
                    _id: "",
                    googleEventId: "",
                    title: "",
                    description: "",
                    startTime: null,
                    endTime: null,
                    attendees: [],
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        async openCreateMeeting() {
            try {
                if (this.accountPlan === "free") {        
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].meetings.create;
    
                    if (this.pageData.total >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    } 

                    if (this.isSearched) {
                        await this.$store.dispatch("app/checkUpgradeStatus", "meetings");
                    }
                }

                if (!this.appIntegrations.length) {
                    console.log("!this.appIntegrations.length");
                    await this.loadIntegrations();
                }
            
                // if user still has no active calendar integrations
                if (!this.calendarIntegrations.length) {
                    this.showIntegrationInfoModal = true;
                    return;
                }

                this.meetingModalType = "add"; 
                this.modalState = "showMeetingModal";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            }
        },

        closeMeetingModal() {
            this.modalState = "";

            if (this.meetingModalType === "edit") {
                this.selectedMeeting = null;
            }
            this.meetingModalType = "";
        },

        async onCompleteCreateMeeting() {  
            await this.handleFetchMeetings({}, false);          
            this.closeMeetingModal();
        },

        async openEditMeetingModal(item) {            
            this.selectedMeeting = item;
            this.meetingModalType = "edit"; 
            this.modalState = "showMeetingModal";
        },

        onCompleteEditMeeting(data) {            
            this.meetings = [ data, ...this.meetings ];
            this.closeMeetingModal();
        },

        async onDuplicateComplete() {            
            await this.handleFetchMeetings({}, false);    
        },

        async loadIntegrations() {
            try {
                this.fetchCalendarsState = "loading";

                await Promise.all([
                    this.$store.dispatch("config/fetchSystemIntegrations", { refresh: true }),
                    this.$store.dispatch("integration/fetchUserIntegrations", { refresh: true }),
                ]);
            } catch (error) {                
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);

                this.fetchCalendarsState = "error";
            } finally {
                this.fetchCalendarsState = "default";
            }
        },

        showSyncModal({ payload, url }) {
            if (!payload || !url) {
                this.$toast.error("Something went wrong");
                return;
            }

            this.modalState = "showSyncModal";
            this.tempMeetingData = { payload, url };
        },

        initiateSync() {
            if (!this.tempMeetingData.payload || !this.tempMeetingData.url) {
                this.$toast.error("Something went wrong");
                return;
            }

            localStorage.setItem("z:auth_type", "meeting");
            localStorage.setItem("z:m_p", JSON.stringify(this.tempMeetingData.payload));

            window.location.href = this.tempMeetingData.url;
        },

        closeSyncModal() {
            this.modalState = "";
            this.tempMeetingData = undefined;
        },
    },
};
</script>